import { useCallback, useEffect, useRef, useState } from "react";

import { UseFormReturn } from "react-hook-form";

import { UseMutationResult } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../shared/Notifications/useNotifications";
import { IPendingPartCategory } from "../../models/PendingCategory/PendingCategory";
import { PartCategoryPosition, PartCategorySide } from "../../models/Category/Category";
import Form from "../base/Form/Form";
import { FormTextField } from "../base/Form/FormTextField";
import { FormNumberField } from "../base/Form/FormNumberField";
import { FormSelect } from "../base/Form/FormSelect";
import { Button } from "../base/Button";
import CategoryDiagramModal from "../CategoryEditor/CategoryDiagramModal";


function parseProfiler(value: number | string | null | undefined): number | null {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

type PendingCategoryFormValues = {
  name: string,
  partOnly: boolean,
  mechOnly: boolean,
  postage: number | null | undefined,
  whCost: number | null | undefined,
  whTime: number | null | undefined,
  disCost: number | null | undefined,
  disTime: number | null | undefined,
  pinnacleName: string,
  rsName: string,
  frontierName: string,
  ucid: string,
  ucidZone: string,
  skuPrefix: string,
  bodyProfiler: number | null | undefined,
  mechanicalProfiler: number | null | undefined,
  side: number,
  position: number,
};

interface PendingCategoryDetailsProps {
  category: IPendingPartCategory | undefined,
  update: UseMutationResult<any, unknown, IPendingPartCategory, unknown>,
  confirm: UseMutationResult<any, unknown, IPendingPartCategory, unknown>,
  open?: boolean,
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>,
};


export default function PendingCategoryDetails({ category, update, confirm, open, setOpen }: PendingCategoryDetailsProps) {
  const navigate = useNavigate();
  const { saveHandlers } = useDefaultCRUDHandlers("Pending category");
  const { addNotification } = useNotification();
  const [diagramOpen, setDiagramOpen] = useState(false);
  const formMethodsRef = useRef<UseFormReturn<PendingCategoryFormValues> | null>(null);

  const handleFormReady = useCallback((methods: UseFormReturn<PendingCategoryFormValues>) => {
    formMethodsRef.current = methods;
  }, []);

  const handleDiagramSave = useCallback((zone: string, partCode: string | null) => {
    const methods = formMethodsRef.current;
    if (!methods) return;

    methods.setValue("ucidZone", zone);

    if (partCode) {
      const currentUcid = methods.getValues("ucid") || "";
      const existing = currentUcid.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (!existing.includes(partCode)) {
        methods.setValue("ucid", existing.length ? `${existing.join(", ")}, ${partCode}` : partCode);
      }
    }
  }, []);

  const partSides = [...Object.values(PartCategorySide).filter((ct) => isNaN(Number(ct)))];
  const partPositions = [...Object.values(PartCategoryPosition).filter((ct) => isNaN(Number(ct)))];

  const buildPayload = (data: any): IPendingPartCategory => ({
    ...(category as IPendingPartCategory),
    ...data,
    side: parseInt(data.side),
    position: parseInt(data.position),
    bodyProfiler: parseProfiler(data.bodyProfiler),
    mechanicalProfiler: parseProfiler(data.mechanicalProfiler),
  });

  // Primary action: confirm & add - promotes the (edited) pending category into
  // the real part categories and removes it from the pending list.
  const onSubmit = (data: any) => {
    if (!category) return;

    confirm.mutate(buildPayload(data), {
      onSuccess: () => {
        addNotification({ variant: "success", primaryText: "Category confirmed & added" });
        navigate("/pending-categories");
      },
      onError: () => {
        addNotification({ variant: "error", primaryText: "Failed to confirm category" });
      },
    });

    setOpen && setOpen(false);
  };

  // Secondary action: save edits but keep it in the pending list.
  const handleSaveChanges = () => {
    const methods = formMethodsRef.current;
    if (!methods || !category) return;

    update.mutate(buildPayload(methods.getValues()), saveHandlers);
  };

  useEffect(() => {
    const methods = formMethodsRef.current;
    if (!methods) return;

    if (category !== undefined) {
      methods.reset({
        name: category.name,
        partOnly: category.partOnly,
        mechOnly: category.mechOnly,
        postage: category.postage,
        whCost: category.whCost,
        whTime: category.whTime,
        disCost: category.disCost,
        disTime: category.disTime,
        pinnacleName: category.pinnacleName,
        rsName: category.rsName,
        frontierName: category.frontierName,
        ucid: category.ucid,
        ucidZone: category.ucidZone,
        skuPrefix: category.skuPrefix,
        bodyProfiler: category.bodyProfiler,
        mechanicalProfiler: category.mechanicalProfiler,
        side: category.side,
        position: category.position,
      });
    } else {
      methods.reset({});
    }
  }, [category, open]);


  return (
  <>
    <Form<PendingCategoryFormValues>
      onSubmit={onSubmit}
      defaultValues={{
        ...category,
      }}
      onReady={handleFormReady}
    >
      {({ register }: UseFormReturn<PendingCategoryFormValues>) => (
        <div className="space-y-4 border-t border-gray-200">
          <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">

            <FormTextField label="Name" {...register("name")} />
            <FormNumberField label="Default Postage (£)" {...register("postage")} step=".01" />

            <FormNumberField label="Default Warehouse Time" {...register("whTime")} />
            <FormNumberField label="Default Warehouse Cost  (£)" {...register("whCost")} step=".01" />

            <FormNumberField label="Default Disassembly Time" {...register("disTime")} />
            <FormNumberField label="Default Disassembly Cost (£)" {...register("disCost")} step=".01" />

            <FormSelect label="Side" {...register("side")}>
              {partSides.map((ct, i) => (
                <option key={ct} value={i}>
                  {ct}
                </option>
              ))}
            </FormSelect>
            <FormSelect label="Position" {...register("position")}>
              {partPositions.map((ct, i) => (
                <option key={ct} value={i}>
                  {ct}
                </option>
              ))}
            </FormSelect>

            <FormTextField label="Pinnacle Name" {...register("pinnacleName")} />
            <FormTextField label="Rs Automotive Name" {...register("rsName")} />
            <FormTextField label="Frontier Name" {...register("frontierName")} />

            <FormNumberField label="Body Profiler" {...register("bodyProfiler")} step=".01" />
            <FormNumberField label="Mechanical Profiler" {...register("mechanicalProfiler")} step=".01" />

            <FormTextField label="SKU Prefix" {...register("skuPrefix")} />
            <FormTextField label="UCID" {...register("ucid")} />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <FormTextField label="UCID Zone" {...register("ucidZone")} />
              </div>
              <Button type="button" onClick={() => setDiagramOpen(true)}>
                Find on Diagram
              </Button>
            </div>


            <div className="flex flex-wrap items-center justify-between">
              <div>
                <input
                  id="bodyPart"
                  type="checkbox"
                  {...register("partOnly")}
                />
                <label htmlFor="bodyPart" className="ml-3 text-sm font-medium text-gray-700">Body Part?</label>
              </div>
              <div>
                <input
                  id="mechPart"
                  type="checkbox"
                  {...register("mechOnly")}
                />
                <label htmlFor="mechPart" className="ml-3 text-sm font-medium text-gray-700">Mechanical Part?</label>
              </div>
            </div>

            <div>
              {/* spacer */}
            </div>

            <div className="flex gap-x-4">
              <Button type="button" variant="outlined" onClick={() => { navigate("/pending-categories"); setOpen && setOpen(false); }}>
                Cancel
              </Button>
              <Button type="button" variant="outlined" onClick={handleSaveChanges}>
                Save Changes
              </Button>
              <Button type="submit">
                Confirm & Add
              </Button>
            </div>

          </div>
        </div>
      )}
    </Form>

    <CategoryDiagramModal
      open={diagramOpen}
      setOpen={setDiagramOpen}
      ucid={category?.ucid ?? ""}
      ucidZone={category?.ucidZone ?? ""}
      onSave={handleDiagramSave}
    />
  </>
  );
}
